import { Container, Item } from 'Components/Grid';
import ThemedDialog from 'Components/_NewElements/Dialog';
import { assertCapabilities, useAppSelector } from 'Store/index';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import React, { isValidElement, useContext } from 'react';
import { INeuroCommit, INeuroDocument } from 'neuro-data-structures';
import { equals, fromPairs } from 'ramda';
import { exists, formatTime, isPartialDate, isTime, partialDateFromDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { uniq } from 'Utility/ramdaReplacement';
import { FormatMessageContext, LocalizerCtx } from 'Containers/Localizer';
import { MenuItem, styled } from '@mui/material';
import Icon from 'Components/_NewElements/Icon';
import { fieldNamesMapper, valueMapper, returnDefaultValues } from './configs';
import colors from '../../../config/theme/colors';
import { ICapabilityContextProps, withCapabilities } from 'Containers/CapabilityHandler';
import PlatformCapabilities from '../../../config/capabilities';
import { sourceIsIntegration } from 'Utility/documentHandling';

const StyledEditChangeRow = styled(Container)(({ theme }) => ({
  padding: '0.2rem 0.4rem',
  '* > &:not(:last-child)': {
    marginBottom: '1rem',
  },
  ':hover': {
    backgroundColor: theme.palette.grey[300],
    borderRadius: '0.2rem',
  },
}));

const reasonIsObject = (reason: unknown): reason is { date: PartialDate; reason: string; reasonOther?: string } => {
  if (
    reason &&
    typeof reason === 'object' &&
    'date' in reason &&
    isPartialDate(reason.date) &&
    'reason' in reason &&
    typeof reason.reason === 'string'
  )
    return true;
  return false;
};

const handleRemoveOrRestoreData = (
  data: IRemoveRestoreData,
): {
  removeDate: number;
  restoreDate: number;
  restorer: string;
  removeInfo: Array<{ field: string; value: PartialDate | string | undefined }> | null;
} => {
  const removeDate = data.removeDate ? new Date(data.removeDate).valueOf() : data.removeTS;
  const restoreDate = data.restoreTS ? data.restoreTS : new Date().valueOf();
  const reasonObject = reasonIsObject(data.removeReason) ? data.removeReason : null;
  const reasonArray = reasonObject
    ? ['removeDate', 'remover', 'reason', 'reasonOther'].map(field => ({
        field,
        value:
          field === 'remover'
            ? data.remover
            : field === 'removeDate'
              ? reasonObject['date']
              : reasonObject[field as 'reason' | 'reasonOther'],
      }))
    : null;

  return { removeDate, restorer: data.restorer || '', restoreDate, removeInfo: reasonArray };
};

interface IRemoveRestoreData {
  removeTS: number;
  removeDate: string;
  remover: string;
  removerOrg: string;
  removeReason: unknown;
  restoreTS: number;
  restorer: string;
  restorerOrg: string;
}

/** Compare commit1 data to commit2 data */
const compareFunction = (commit1: INeuroCommit | null, commit2: INeuroCommit) => {
  const data1 = (commit1 && typeof commit1.data === 'object' && (commit1.data as Record<string, TFieldValue>)) || null;
  const data2 = (typeof commit2.data === 'object' && (commit2.data as Record<string, TFieldValue>)) || null;
  const fields = [...(data1 ? Object.keys(data1) : []), ...(data2 ? Object.keys(data2) : [])];

  const changes: Array<{ name: string; prev: TFieldValue; new: TFieldValue }> = [];
  (uniq(fields) as string[]).forEach(field => {
    if (!exists(data1?.[field]) && exists(data2?.[field])) {
      changes.push({ name: field, prev: null, new: data2?.[field] });
      return;
    }

    if (exists(data1?.[field]) && !exists(data2?.[field])) {
      changes.push({ name: field, prev: data1?.[field], new: null });
      return;
    }

    if (!exists(data1?.[field]) && !exists(data2?.[field])) return;

    if (!equals(data1?.[field], data2?.[field])) {
      changes.push({ name: field, prev: data1?.[field], new: data2?.[field] });
    }
  });
  return changes;
};

const HistoryContents = ({ docType, prevCommit, thisCommit, fm, firstVersion }: IHistoryContents) => {
  const changedData = compareFunction(prevCommit, thisCommit);
  const { locale } = useContext(LocalizerCtx);
  const platform = useAppSelector(s => s.session.platforms?.selected);

  const fieldNamesMapperThisDoc = fieldNamesMapper(docType, platform);

  const valueFormatter =
    (docType: string) =>
    (fieldName: string, value: TFieldValue): string | number | React.JSX.Element => {
      if (!exists(value) || (Array.isArray(value) && value.length === 0)) return '-';

      // Check if the doctype has some mapped values/options. Could also return some generic localizations
      const optionValueMapped = valueMapper(docType, platform)?.(fieldName, value);
      if (optionValueMapped) {
        if (Array.isArray(optionValueMapped)) {
          /** Object array, event or some other */
          if (optionValueMapped.some(ovm => typeof ovm === 'object')) {
            // First localize keys and values
            const localizedArray = ((optionValueMapped as Record<string, TFieldValue>[]) || []).map(ovm => {
              return fromPairs(
                Object.keys(ovm).map(key => {
                  const mapValue: TFieldValue = ovm[key];
                  const value =
                    key === '_id'
                      ? mapValue
                      : isPartialDate(mapValue)
                        ? formatPartialDate(mapValue)
                        : isTime(mapValue)
                          ? formatTime(mapValue)
                          : Array.isArray(mapValue)
                            ? mapValue
                                .map((val: string | undefined | number | boolean | object) =>
                                  typeof val === 'string' ? fm(val) : val,
                                )
                                .join(', ')
                            : typeof mapValue === 'string'
                              ? fm(mapValue)
                              : mapValue;

                  return [fm(key), value];
                }),
              );
            });
            // Render array as an element
            return (
              <>
                {localizedArray.map((la, i) => {
                  const fieldNames = Object.keys(la);
                  return (
                    <Container
                      key={'id' in la ? `${la.id}` : `${i}`}
                      style={{
                        padding: '0.5rem',
                        margin: '0.5rem 0',
                        backgroundColor: colors.appBlue.lightest + 'aa',
                        borderRadius: '0.5rem',
                      }}
                    >
                      <Item>
                        {fieldNames.map(fn => {
                          const value = typeof la[fn] !== 'object' && la[fn];
                          return (
                            fn !== 'id' && (
                              <Container key={fn}>
                                <Item style={{ fontWeight: 400, paddingRight: '2rem' }}>{fn}</Item>
                                <Item>{value}</Item>
                              </Container>
                            )
                          );
                        })}
                      </Item>
                    </Container>
                  );
                })}
              </>
            );
          }
          /** Simple string array */
          return <>{(optionValueMapped as string[]).map(val => fm(val)).join(', ')}</>;
        }
        if (typeof optionValueMapped === 'object' && !isValidElement(optionValueMapped)) {
          const optionObject: Record<string, string> = optionValueMapped as Record<string, string>;
          return (
            <ul style={{ margin: 0 }}>
              {Object.keys(optionObject).map(ov => {
                const value: string = optionObject[ov];
                return (
                  exists(value) && (
                    <li key={ov}>
                      <span style={{ fontWeight: 400 }}>{fm(ov, false, true)}</span>: <span>{fm(value)}</span>
                    </li>
                  )
                );
              })}
            </ul>
          );
        }
        return typeof optionValueMapped === 'string'
          ? `${fm(optionValueMapped)}`
          : typeof optionValueMapped === 'number'
            ? optionValueMapped
            : '';
      }
      return exists(returnDefaultValues(value))
        ? returnDefaultValues(value)!
        : typeof value === 'object'
          ? 'notConfigured'
          : '';
    };

  return (
    <div style={{ marginBottom: '1rem' }}>
      {changedData.length === 0 && (
        <div style={{ fontStyle: 'italic', textAlign: 'center', width: '100%' }}>Ei muutoksia</div>
      )}
      {changedData.map(changed => {
        // Map field name to possibly localizable strings
        const fieldNameMapped = fieldNamesMapperThisDoc?.(changed.name, locale) || '';
        // Localize field name (string/string[])
        const fieldNameLocalized = Array.isArray(fieldNameMapped)
          ? fieldNameMapped.map(fn => fm(fn, false, true)).join(', ')
          : fm(fieldNameMapped, false, true);

        // Show the not configured (user edited this value, or something like that) text string if the new value is something we cant show (yet)
        const notConfigured = valueFormatter(docType)(changed.name, changed.new) === 'notConfigured';

        return (
          fieldNameMapped && (
            <StyledEditChangeRow key={changed.name} alignItems={'center'}>
              <Item xs={4}>
                <div>{fieldNameLocalized}</div>
              </Item>
              <Item xs={8}>
                <Container justifyContent={'center'}>
                  {notConfigured ? (
                    <div style={{ fontStyle: 'italic', fontWeight: 400 }}>{fm('general.notConfigured')}</div>
                  ) : (
                    <>
                      <>
                        <Item xs={true} style={{ alignSelf: 'center' }}>
                          {!firstVersion && (
                            <Container style={{ fontWeight: 600, justifyContent: 'flex-end' }}>
                              {valueFormatter(docType)(changed.name, changed.prev)}
                            </Container>
                          )}
                        </Item>
                        <Item xs={1} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Icon icon="altRightArrow" />
                        </Item>
                      </>

                      <Item xs={true} style={{ alignSelf: 'center' }}>
                        <div style={{ fontWeight: 600 }}>{valueFormatter(docType)(changed.name, changed.new)}</div>
                      </Item>
                    </>
                  )}
                </Container>
              </Item>
            </StyledEditChangeRow>
          )
        );
      })}
    </div>
  );
};

interface IHistoryContents {
  docType: string;
  prevCommit: INeuroCommit | null;
  thisCommit: INeuroCommit;
  fm: (id: string, removeLocalePath?: boolean, warnAboutMissingLocale?: boolean) => string;
  firstVersion?: boolean;
}

const formatTimeStamp = (timestamp: number) => {
  const date = new Date(timestamp);
  const minutes = date.getMinutes().toString().length === 1 ? `0${date.getMinutes()}` : date.getMinutes();
  return (
    <Container>
      <Item
        xs={'auto'}
        style={{ marginRight: '1rem' }}
      >{`${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`}</Item>
      <Item xs={true}>{`${date.getHours()}:${minutes}`}</Item>
    </Container>
  );
};

const EditHistoryMenuItemComp = ({ document, capabilityGroups = {} }: IEditHistoryMenuItem) => {
  const { formatMessage } = useContext(FormatMessageContext);
  const [open, setOpen] = React.useState<boolean>(false);
  if (!document) return null;

  const editHistoryCapability = assertCapabilities([PlatformCapabilities.EDITLOG_DELETELOG_VIEW], capabilityGroups);

  /** Set the menuitem as disabled if no capability or no config exists for the document type */
  const disabled = !editHistoryCapability || !fieldNamesMapper(document._type);
  return (
    <>
      <MenuItem disabled={disabled}>
        <Container
          alignItems={'center'}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            !disabled && setOpen(true);
          }}
        >
          <Item style={{ padding: '0 1rem 0 0', width: '3rem' }}>
            <Icon icon="description" size={1.8} color={disabled ? 'gray' : undefined} />
          </Item>
          <Item xs={true}>{formatMessage('general.editHistory')}</Item>
        </Container>
      </MenuItem>
      <EditHistoryDialog open={open} closeDialog={() => setOpen(false)} document={document} />
    </>
  );
};

interface IEditHistoryMenuItem extends ICapabilityContextProps {
  document?: IOwnProps['document'];
  iconSize?: number;
}
export const EditHistoryMenuItem = withCapabilities(EditHistoryMenuItemComp);

/**
 * Returns an empty array when the document is missing, so callers can pass
 * the result straight to HistorySection's `menuItems` without the menu
 * icon appearing for a non-existent document.
 */
export const editHistoryMenuItems = (document?: IOwnProps['document']): React.JSX.Element[] =>
  document ? [<EditHistoryMenuItem key="edit" document={document} />] : [];

const EditHistoryDialogStandAloneComp = ({
  document,
  iconSize,
  relative = true,
  capabilityGroups = {},
  customStyle,
}: IEditHistoryDialogStandAlone) => {
  const { formatMessage } = useContext(FormatMessageContext);
  const [open, setOpen] = React.useState<boolean>(false);
  if (!document) return null;

  const editHistoryCapability = assertCapabilities([PlatformCapabilities.EDITLOG_DELETELOG_VIEW], capabilityGroups);
  const disabled = !editHistoryCapability || !fieldNamesMapper(document._type);

  return (
    /** Only show the editing history button if capability exists */
    editHistoryCapability && (
      <>
        <button
          style={{
            cursor: disabled ? 'default' : 'pointer',
            padding: '0 0.7rem',
            fontSize: '1.6rem',
            color: disabled ? colors.darkGray : colors.appBlue.default,
            fontWeight: 600,
            display: 'flex',
            position: relative ? 'relative' : 'inherit',
            top: '2.5rem',
            height: relative ? '0' : undefined,
            justifySelf: 'center',
            border: 'none',
            backgroundColor: 'transparent',
            fontFamily: 'inherit',
            ...customStyle,
          }}
          onClick={() => !disabled && setOpen(true)}
        >
          <div style={{ marginRight: '0.5rem' }}>
            <Icon icon="description" size={iconSize} color={disabled ? 'darkGrey' : undefined} />
          </div>
          <div>{formatMessage('general.editHistory')}</div>
        </button>
        <EditHistoryDialog open={open} closeDialog={() => setOpen(false)} document={document} />
      </>
    )
  );
};

interface IEditHistoryDialogStandAlone extends ICapabilityContextProps {
  document?: IOwnProps['document'];
  iconSize?: number;
  relative?: boolean;
  customStyle?: React.CSSProperties;
}

export const EditHistoryDialogStandAlone = withCapabilities(EditHistoryDialogStandAloneComp);

const EditHistoryDialog = ({ open, document, closeDialog }: IOwnProps) => {
  const [collapseIds, setCollapseIds] = React.useState<string[]>([]);
  const collapseHandler = (id: string) => {
    if (collapseIds.includes(id)) setCollapseIds(collapseIds.filter(cid => cid !== id));
    else setCollapseIds([...collapseIds, id]);
  };

  const users = useAppSelector(s => s.session.orgUsers) || [];
  const getUserNameFromCommit = (commit: INeuroCommit) => {
    if (sourceIsIntegration(commit.source)) return formatMessage('general.integration');
    return getUserNameById(commit.creatorId);
  };

  const getUserNameById = (id: string) => {
    const thisUser = users.find(u => u.userId === id);
    if (!thisUser) return formatMessage('general.unknownUser');
    return `${thisUser.firstNames} ${thisUser.lastNames}`;
  };

  const actualDocuments = useAppSelector(s => s.documents.documents) || [];
  const actualFiles = useAppSelector(s => s.documents.neuroFiles) || [];

  let thisActualDocument: INeuroDocument | undefined;

  if (document._type === 'filestorefile') {
    thisActualDocument = actualFiles.find(d => d.documentId === document._id);
  } else {
    thisActualDocument = actualDocuments.find(d => d.documentId === document._id);
  }

  const restoreData = thisActualDocument?.restoreHistory || [];

  const formattedRestoreData = restoreData.map(res =>
    handleRemoveOrRestoreData({
      ...res,
      remover: getUserNameById(res.remover),
      restorer: getUserNameById(res.restorer),
    }),
  );

  // Get finished commits
  const relevantCommits = thisActualDocument?.commits.filter(com => com.commitDate) || [];
  const sortedCommitsWithRestoreData = [...relevantCommits, ...formattedRestoreData].sort((c1, c2) => {
    const date1 = 'restoreDate' in c1 ? (c1.restoreDate as number) : (c1 as INeuroCommit).commitDate || 0;
    const date2 = 'restoreDate' in c2 ? (c2.restoreDate as number) : (c2 as INeuroCommit).commitDate || 0;

    return date2 - date1;
  });

  const { formatMessage } = useContext(FormatMessageContext);

  return (
    <ThemedDialog
      open={open}
      onClose={closeDialog}
      title={formatMessage('general.editHistory')}
      dialogActions={[{ text: 'general.close', onClick: closeDialog }]}
      dialogProps={{ maxWidth: 'md' }}
    >
      {thisActualDocument &&
        sortedCommitsWithRestoreData.map((editCommitData, i) => {
          const firstVersion = i === sortedCommitsWithRestoreData.length - 1;
          if ('restoreDate' in editCommitData) {
            const restoreData = editCommitData as unknown as ReturnType<typeof handleRemoveOrRestoreData>;
            return (
              <>
                <BlockWrapper
                  key={restoreData.restoreDate}
                  collapse={{
                    collapseHandler: () => collapseHandler(`${restoreData.restoreDate}`),
                    open: collapseIds.includes(`${restoreData.restoreDate}`),
                  }}
                  title={
                    <Container>
                      <Item xs={3} md={2}>
                        {formatTimeStamp(restoreData.restoreDate)}
                      </Item>

                      <Item style={{ fontWeight: 400, fontSize: '1.6rem' }}>
                        {formatMessage('general.recoveringDoc')}
                      </Item>

                      <Item xs={true}>
                        <div
                          style={{ display: 'flex', justifyContent: 'flex-end', fontWeight: 400, fontSize: '1.6rem' }}
                        >
                          {restoreData.restorer}
                        </div>
                      </Item>
                    </Container>
                  }
                  color="warning"
                >
                  <div>
                    {(restoreData.removeInfo || []).map(data => (
                      <Container key={data.field} style={{ padding: '0.2rem 0.4rem', marginBottom: '1rem' }}>
                        <Item xs={4}>{formatMessage('general.' + data.field)}</Item>
                        <Item style={{ fontWeight: 600 }}>
                          {data.field === 'removeDate'
                            ? isPartialDate(data.value)
                              ? formatPartialDate(data.value)
                              : formatPartialDate(partialDateFromDate(new Date(restoreData.removeDate)))
                            : data.field === 'reason' && data.value
                              ? formatMessage('general.opts.' + data.value)
                              : data.value || '-'}
                        </Item>
                      </Container>
                    ))}
                  </div>
                </BlockWrapper>
              </>
            );
          } else {
            const editCommit = editCommitData as INeuroCommit;
            const sortedCommits = sortedCommitsWithRestoreData.filter(
              com => !('restoreDate' in com),
            ) as Array<INeuroCommit>;
            return (
              <BlockWrapper
                key={editCommit.commitId}
                collapse={{
                  collapseHandler: () => collapseHandler(editCommit.commitId),
                  open: collapseIds.includes(editCommit.commitId),
                }}
                title={
                  <Container>
                    {editCommit.commitDate && (
                      <Item xs={3} md={2}>
                        {formatTimeStamp(editCommit.commitDate)}
                      </Item>
                    )}
                    {firstVersion && (
                      <Item style={{ fontWeight: 400, fontSize: '1.6rem' }}>
                        {formatMessage('general.firstVersion')}
                      </Item>
                    )}
                    <Item xs={true}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', fontWeight: 400, fontSize: '1.6rem' }}>
                        {getUserNameFromCommit(editCommit)}
                      </div>
                    </Item>
                  </Container>
                }
              >
                <HistoryContents
                  docType={thisActualDocument.documentType}
                  prevCommit={!firstVersion ? sortedCommits[i + 1] : null}
                  thisCommit={editCommit}
                  fm={formatMessage}
                  firstVersion={firstVersion}
                />
              </BlockWrapper>
            );
          }
        })}
    </ThemedDialog>
  );
};

interface IOwnProps {
  document: IControlProps;
  open: boolean;
  closeDialog: () => void;
}

export default EditHistoryDialog;
